<div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
  <div class="w-full max-w-2xl bg-[#0d1117] rounded-2xl shadow-2xl border border-white/10 overflow-hidden flex flex-col" style="max-height: 85vh">

    <!-- Title bar -->
    <div class="flex items-center gap-3 px-5 py-3 bg-[#161b22] border-b border-white/10 flex-shrink-0">
      <span class="text-white/50 text-xs font-mono flex-1 text-center">sportiq — {{ isRegenerateMode ? 'régénération' : 'génération' }} licences {{ entityLabel }}</span>
      @if (isConfirming() || isDone()) {
        <button (click)="close()" class="text-white/40 hover:text-white/80 transition-colors">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      }
    </div>

    <!-- Confirm screen -->
    @if (isConfirming()) {
      <div class="flex-1 flex flex-col items-center justify-center gap-6 px-8 py-10">
        @if (isRegenerateMode) {
          <div class="w-14 h-14 rounded-2xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
            <svg class="w-7 h-7 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"/>
            </svg>
          </div>
        } @else {
          <div class="w-14 h-14 rounded-2xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center">
            <svg class="w-7 h-7 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
            </svg>
          </div>
        }
        <div class="text-center space-y-2">
          @if (isRegenerateMode) {
            <p class="text-white/90 font-semibold text-base">Régénérer toutes les licences {{ entityLabel }} ?</p>
            <p class="text-white/40 text-sm">Toutes les licences déjà générées avec des demandes de <span class="text-orange-400/80 font-semibold">modification</span> seront régénérées.<br>Le numéro de licence reste inchangé — seules les données et le PDF sont mis à jour.</p>
          } @else {
            <p class="text-white/90 font-semibold text-base">Générer toutes les licences {{ entityLabel }} ?</p>
            <p class="text-white/40 text-sm">Toutes les demandes au statut « <span class="text-yellow-400/80 font-semibold">{{ fedValidatedLabel() }}</span> » seront traitées séquentiellement.<br>Cette action ne peut pas être annulée en cours d'exécution.</p>
          }
        </div>
        <div class="flex items-center gap-3">
          <button
            (click)="close()"
            class="px-5 py-2 rounded-lg bg-white/8 hover:bg-white/15 text-white/60 hover:text-white/90 text-sm font-medium transition-all duration-200"
          >
            Annuler
          </button>
          <button
            (click)="confirm()"
            class="px-6 py-2 rounded-lg bg-green-500 hover:bg-green-400 text-white text-sm font-semibold shadow-lg shadow-green-500/20 transition-all duration-200"
          >
            Confirmer
          </button>
        </div>
      </div>
    }

    <!-- Terminal body -->
    @if (!isConfirming()) {
      <div id="terminal-body" class="flex-1 overflow-y-auto p-5 font-mono text-sm space-y-1 min-h-0" style="font-size:13px">

        <!-- Prompt line at top -->
        <div class="flex items-center gap-2 text-green-400 mb-3">
          <span class="text-purple-400">sportiq</span>
          <span class="text-white/30">~</span>
          <span class="text-blue-400">$</span>
          <span class="text-white/80">licence {{ isRegenerateMode ? 'regenerate-all' : 'generate-all' }} --type={{ entityType }}</span>
          @if (isRunning()) {
            <span class="inline-block w-2 h-4 bg-green-400 ml-1 animate-pulse"></span>
          }
        </div>

        <!-- Log lines -->
        @for (line of lines(); track $index) {
          <div class="flex items-start gap-3 animate-fade-in leading-relaxed">
            @if (line.kind === 'info') {
              <span class="text-blue-400 flex-shrink-0">ℹ</span>
              <span class="text-white/70">{{ line.text }}</span>
            }
            @if (line.kind === 'success') {
              <span class="text-green-400 flex-shrink-0">✓</span>
              <span class="text-white/80">{{ line.text }}</span>
              <span class="text-green-300 font-semibold ml-auto flex-shrink-0">{{ line.licence }}</span>
            }
            @if (line.kind === 'error') {
              <span class="text-red-400 flex-shrink-0">✗</span>
              <span class="text-red-300/80">{{ line.text }}</span>
            }
            @if (line.kind === 'done') {
              <div class="w-full mt-3 pt-3 border-t border-white/10">
                <div class="flex items-center gap-3">
                  <span class="text-white/30 flex-shrink-0">—</span>
                  <span class="text-white/60">Terminé ·</span>
                  <span class="text-green-400 font-semibold">{{ line.generated }} {{ isRegenerateMode ? 'régénérée(s)' : 'générée(s)' }}</span>
                  @if (line.failed > 0) {
                    <span class="text-white/40">·</span>
                    <span class="text-red-400">{{ line.failed }} échouée(s)</span>
                  }
                  <span class="text-white/40">/ {{ line.total }} total</span>
                </div>
              </div>
            }
          </div>
        }

        <!-- Blinking cursor while running with no lines yet -->
        @if (isRunning() && lines().length === 0) {
          <div class="flex items-center gap-2 text-white/40">
            <span class="inline-block w-2 h-4 bg-white/50 animate-pulse"></span>
          </div>
        }
      </div>

      <!-- Footer -->
      <div class="bg-[#161b22] border-t border-white/10 flex-shrink-0">
        @if (isRunning() && totalCount() > 0) {
          <div class="px-5 pt-3 pb-1">
            <div class="flex items-center justify-between mb-1">
              <span class="text-[10px] font-mono text-white/30">{{ processedCount() }} / {{ totalCount() }}</span>
              <span class="text-[10px] font-mono text-white/50 font-semibold">{{ progressPct() }}%</span>
            </div>
            <div class="w-full h-1 bg-white/10 rounded-full overflow-hidden">
              <div class="h-full rounded-full transition-all duration-300" [class]="isRegenerateMode ? 'bg-orange-500' : 'bg-green-500'" [style.width.%]="progressPct()"></div>
            </div>
          </div>
        }
        <div class="flex items-center justify-between px-5 py-3">
        <div class="text-xs text-white/30 font-mono">
          @if (isRunning() && !isStopping()) {
            <span class="text-yellow-400/70 animate-pulse">● en cours…</span>
          } @else if (isStopping()) {
            <span class="text-orange-400/70 animate-pulse">● arrêt en cours…</span>
          } @else if (isDone()) {
            <span class="text-green-400/70">● terminé</span>
          }
        </div>
        <div class="flex items-center gap-2">
          @if (isRunning()) {
            <button
              (click)="stop()"
              [disabled]="isStopping()"
              class="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/20"
            >
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5.25 7.5A2.25 2.25 0 017.5 5.25h9a2.25 2.25 0 012.25 2.25v9a2.25 2.25 0 01-2.25 2.25h-9a2.25 2.25 0 01-2.25-2.25v-9z"/>
              </svg>
              {{ isStopping() ? 'Arrêt…' : 'Stop' }}
            </button>
          }
          @if (isDone()) {
            <button
              (click)="downloadLogs()"
              class="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/8 hover:bg-white/15 text-white/50 hover:text-white/80 text-sm font-medium transition-all duration-200"
            >
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"/>
              </svg>
              Logs
            </button>
            <button
              (click)="close()"
              class="px-5 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 hover:text-white text-sm font-medium transition-all duration-200"
            >
              Fermer
            </button>
          }
        </div>
        </div>
      </div>
    }

  </div>
</div>
